import { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheck, Menu, X, ArrowUpRight, Wrench, ChevronDown } from 'lucide-react';
import { NAV_LINKS } from '../data/content';

export const Navbar = () => {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();

    useEffect(() => { setOpen(false); }, [location.pathname]);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    return (
        <>
            <header className="fixed top-0 left-0 right-0 z-50 px-4 sm:px-6">
                <motion.nav
                    initial={{ y: -60, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                    className={`mx-auto mt-4 max-w-6xl flex items-center justify-between rounded-full border px-5 sm:px-6 py-3 transition-colors duration-300 ${scrolled ? 'bg-navy-900/85 backdrop-blur-xl border-slate-700 shadow-2xl shadow-black/40' : 'bg-navy-900/40 backdrop-blur-md border-slate-800'}`}
                    data-testid="main-navigation"
                >
                    <Link to="/" className="flex items-center gap-2.5 group" data-testid="nav-logo-link" aria-label="Planmodo home">
                        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-deepteal/10 border border-deepteal/30 text-deepteal transition-colors group-hover:bg-glow/20">
                            <ShieldCheck size={18} strokeWidth={2.2} />
                        </span>
                        <span className="font-display text-lg font-bold tracking-tight text-slate-50">Planmodo</span>
                    </Link>

                    <div className="hidden lg:flex items-center gap-1">
                        {NAV_LINKS.map((link) => (
                            <NavLink
                                key={link.path}
                                to={link.path}
                                data-testid={`nav-${link.label.toLowerCase()}-link`}
                                className={({ isActive }) =>
                                    `px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${isActive ? 'text-deepteal bg-deepteal/10' : 'text-slate-300 hover:text-slate-50 hover:bg-slate-700'}`
                                }
                            >
                                {link.label}
                            </NavLink>
                        ))}
                    </div>

                    <div className="flex items-center gap-3">
                        <Link
                            to="/contact"
                            data-testid="nav-cta-consultation"
                            className="hidden lg:inline-flex items-center gap-1.5 rounded-full bg-glow px-5 py-2.5 text-sm font-semibold text-ink transition-transform duration-200 hover:scale-105 hover:shadow-lg hover:shadow-glow/25"
                        >
                            Schedule a Consultation
                            <ArrowUpRight size={15} />
                        </Link>
                        <div className="relative group hidden lg:block" data-testid="nav-self-help-tools">
                            <button
                                type="button"
                                data-testid="nav-self-help-tools-link"
                                className="inline-flex items-center gap-1.5 rounded-full border border-slate-500 px-4 py-2.5 text-sm font-medium text-slate-200 transition-colors duration-200 hover:border-deepteal/50 hover:text-deepteal"
                            >
                                <Wrench size={14} />
                                Self-Help Tools
                                <ChevronDown size={13} className="transition-transform duration-200 group-hover:rotate-180" />
                            </button>
                            <div className="invisible absolute right-0 top-full z-50 pt-3 opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
                                <div className="w-72 rounded-2xl border border-slate-700 bg-white p-2 shadow-xl shadow-slate-900/5">
                                    <a href="/tools/bcp-program-estimator.html" target="_blank" rel="noopener noreferrer" data-testid="nav-tool-bcp-estimator" className="block rounded-xl px-4 py-3 transition-colors hover:bg-deepteal/5">
                                        <span className="flex items-center gap-2 text-sm font-semibold text-slate-50">BCP Program Estimator <ArrowUpRight size={13} className="text-deepteal" /></span>
                                        <span className="mt-1 block text-xs leading-relaxed text-slate-400">Estimate the effort, timeline, and cost of a continuity program.</span>
                                    </a>
                                    <Link to="/services#readiness-calculator" data-testid="nav-tool-readiness" className="block rounded-xl px-4 py-3 transition-colors hover:bg-deepteal/5">
                                        <span className="flex items-center gap-2 text-sm font-semibold text-slate-50">Readiness Self-Assessment <ArrowUpRight size={13} className="text-deepteal" /></span>
                                        <span className="mt-1 block text-xs leading-relaxed text-slate-400">Score your organization's readiness in ninety seconds.</span>
                                    </Link>
                                </div>
                            </div>
                        </div>
                        <button
                            className="lg:hidden flex h-10 w-10 items-center justify-center rounded-full border border-slate-700 text-slate-200"
                            onClick={() => setOpen(!open)}
                            data-testid="mobile-menu-toggle"
                            aria-label="Toggle menu"
                        >
                            {open ? <X size={20} /> : <Menu size={20} />}
                        </button>
                    </div>
                </motion.nav>
            </header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="fixed inset-0 z-40 bg-navy-950/95 backdrop-blur-xl lg:hidden"
                        data-testid="mobile-menu"
                    >
                        <div className="flex h-full flex-col justify-center px-8">
                            {NAV_LINKS.map((link, i) => (
                                <motion.div
                                    key={link.path}
                                    initial={{ opacity: 0, x: -24 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                                >
                                    <NavLink
                                        to={link.path}
                                        data-testid={`mobile-nav-${link.label.toLowerCase()}-link`}
                                        className={({ isActive }) =>
                                            `block border-b border-slate-800 py-5 font-display text-3xl font-bold ${isActive ? 'text-deepteal' : 'text-slate-100'}`
                                        }
                                    >
                                        {link.label}
                                    </NavLink>
                                </motion.div>
                            ))}
                            <motion.div
                                initial={{ opacity: 0, y: 16 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.45 }}
                                className="pt-8"
                            >
                                <Link
                                    to="/contact"
                                    data-testid="mobile-nav-cta-consultation"
                                    className="inline-flex items-center gap-2 rounded-full bg-glow px-7 py-3.5 font-semibold text-ink"
                                >
                                    Schedule a Consultation <ArrowUpRight size={16} />
                                </Link>
                                <div className="mt-4">
                                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Self-help tools</p>
                                    <div className="mt-3 flex flex-col items-start gap-3">
                                        <a
                                            href="/tools/bcp-program-estimator.html"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            data-testid="mobile-nav-tool-bcp-estimator"
                                            className="inline-flex items-center gap-2 rounded-full border border-slate-500 px-6 py-3 text-sm font-semibold text-slate-100 transition-colors hover:border-deepteal/50 hover:text-deepteal"
                                        >
                                            <Wrench size={15} /> BCP Program Estimator
                                        </a>
                                        <Link
                                            to="/services#readiness-calculator"
                                            data-testid="mobile-nav-tool-readiness"
                                            className="inline-flex items-center gap-2 rounded-full border border-slate-500 px-6 py-3 text-sm font-semibold text-slate-100 transition-colors hover:border-deepteal/50 hover:text-deepteal"
                                        >
                                            <Wrench size={15} /> Readiness Self-Assessment
                                        </Link>
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};
